import { ChangeDetectionStrategy, Component, Injector, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { PageChangeEvent, PaginatedSearchHeaderComponentModule } from '@shared/ui';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';

import { EDITOR_DIALOG_LARGE } from '../../editor-dialog-sizes';
import { MethodTypeEditorOutcome } from '../../../state/method-type-editor.state';
import { MethodTypeItem } from '../../../state/method-type-list.state';
import {
  MethodTypeEditorDialogComponent,
  MethodTypeEditorDialogData,
} from '../method-type-editor/method-type-editor-dialog.component';
import { MethodTypeColumn, MethodTypeListItem, MethodTypesListComponentModule } from '../method-types-list.component';

import { MethodTypeManagerFacade } from './method-type-manager.facade';

/**
 * Smart two-list manager for the method types assigned to a case type — the
 * method-type analog of `MethodPipelineManagerComponent` / `ResourceManagerComponent`.
 * Left list: all available method types (catalog + staged drafts), each row's
 * `assigned` toggle adds/removes it from the case type. Right list: the assigned
 * method types, with an edit affordance opening the method-type editor dialog.
 * The "NEW METHOD TYPE" button opens the editor dialog in create mode.
 *
 * The component owns NO providers; the host supplies the full DI scope (the
 * manager catalog + facade via `methodTypeManagerProviders`, the shared
 * staged/assigned ledgers, and the method-type editor session scope the dialog
 * resolves against).
 *
 * NOTE (assembly concern): the editor dialog and its embedded pipeline/resource
 * managers read their context tokens once at construction. Editing the full
 * detail (pipelines/resources) of multiple distinct method types within one
 * manager session needs per-open context wiring that the case-type editor page
 * must compose — see the manager providers doc. The manager's own job (which
 * method types are assigned, plus each method type's core draft) is fully
 * handled here.
 */
@Component({
  selector: 'ts-method-type-manager',
  templateUrl: './method-type-manager.component.html',
  styleUrls: ['./method-type-manager.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    MatDialogModule,
    PaginatedSearchHeaderComponentModule,
    MethodTypesListComponentModule,
  ],
})
export class MethodTypeManagerComponent implements OnInit, OnDestroy {
  public readonly leftColumns = [MethodTypeColumn.NAME, MethodTypeColumn.ASSIGNED];
  public readonly rightColumns = [MethodTypeColumn.NAME, MethodTypeColumn.EDIT, MethodTypeColumn.ASSIGNED];

  // Catalog page size is locked at 10 (left list).
  public readonly pageSizeOptions = [10];

  private readonly destroy$ = new Subject<void>();

  constructor(
    public readonly facade: MethodTypeManagerFacade,
    private readonly dialog: MatDialog,
    private readonly injector: Injector
  ) {}

  ngOnInit(): void {
    this.facade.initialize();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public searchTermChanged(value: string): void {
    this.facade.search(value);
  }

  public handlePageEvent(event: PageChangeEvent<MethodTypeItem>): void {
    this.facade.loadMore(event.nextToken);
  }

  public onToggleAvailable(item: MethodTypeListItem): void {
    this.facade.toggleAvailable(item);
  }

  public onUnassign(item: MethodTypeListItem): void {
    this.facade.unassign(item);
  }

  public onNewMethodType(): void {
    // Mint the placeholder id HERE so the same id seeds the create session AND
    // scopes the dialog's embedded pipeline/resource managers to this new,
    // not-yet-persisted method type (empty pipelines, empty resource tree).
    const methodTypeId = `tmp-${uuidv4()}`;
    this.facade.beginCreateMethodType(methodTypeId);
    this.openEditorDialog({ methodTypeId, persisted: false });
  }

  public onEditMethodType(item: MethodTypeListItem): void {
    this.facade.beginEditMethodType(item);
    this.openEditorDialog({ methodTypeId: item.id, persisted: !item.pending });
  }

  private openEditorDialog(data: MethodTypeEditorDialogData): void {
    this.dialog
      .open<MethodTypeEditorDialogComponent, MethodTypeEditorDialogData, MethodTypeEditorOutcome | undefined>(
        MethodTypeEditorDialogComponent,
        {
          ...EDITOR_DIALOG_LARGE,
          hasBackdrop: true,
          injector: this.injector,
          data,
        }
      )
      .afterClosed()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((outcome) => {
        if (outcome?.kind === 'saved') {
          this.facade.stageSavedMethodType();
        }
      });
  }
}
